/**
 * Rear-area casualty decay configuration (moved out of casualtyCalculator.js on 2026-10-11, Dr. Tatar).
 *
 * Applies ONLY to the rear-area casualty component (support units behind the front). Frontline casualties of a unit do
 * not depend on its distance from the front. Further from the front line, and with better air defense, rear-area
 * casualties fall; at what rate is an EDUCATED GUESS (no data by distance band was found; see
 * docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4). In modern war drones and rockets reach hundreds of kilometers, so the outer
 * bands are the least certain. TECHNOLOGY_ENVIRONMENT.rear_area_multiplier is designed for the drone case and is not wired
 * yet (decision pending).
 * Qualitative support only: FPV drone kill zone up to 20 km from the front (AP, 2025); brigade support area 10 to 30 km
 * from the front line (US Army); deep strikes beyond 50 km increasing (West Point MWI).
 */
export const REAR_AREA_DECAY_STATUS = {
  status: 'EDUCATED_GUESS',
  basis: 'Distance bands, threat environment factors and unit vulnerability factors have no source; qualitative support only.'
};

/**
 * CC-012: Rear Area Casualty Decay Configuration
 * 
 * Units further from the FEBA experience fewer rear area attacks.
 * Based on Ukraine 2022-2024 data showing:
 * - 0-15km: Full rear area threat (artillery, drones, rockets)
 * - 15-30km: Reduced threat (mainly drones, some artillery)
 * - 30-60km: Low threat (mainly drones, occasional missiles)
 * - 60-100km: Minimal threat (strategic strikes only)
 * - 100km+: Very low threat (rare strategic attacks)
 * 
 * Research: PMC 2023 "Casualty care implications of large-scale combat"
 */
export const REAR_AREA_DECAY_CONFIG = {
  // Distance bands with decay multipliers
  distance_bands: [
    { max_km: 15, multiplier: 1.0, threat_level: 'FULL', description: 'Full artillery/drone/rocket threat' },
    { max_km: 30, multiplier: 0.70, threat_level: 'HIGH', description: 'Reduced artillery, full drone threat' },
    { max_km: 60, multiplier: 0.40, threat_level: 'MODERATE', description: 'Drone threat, occasional missiles' },
    { max_km: 100, multiplier: 0.20, threat_level: 'LOW', description: 'Strategic strikes only' },
    { max_km: Infinity, multiplier: 0.10, threat_level: 'MINIMAL', description: 'Rare strategic attacks' }
  ],
  
  // Threat environment modifiers (multiplied with distance decay)
  threat_environment: {
    PEER: 1.0,        // Full peer adversary (Russia-level drone/missile capability)
    NEAR_PEER: 0.7,   // Near-peer (reduced long-range strike capability)
    REGIONAL: 0.4,    // Regional threat (limited precision strike)
    IRREGULAR: 0.2    // Irregular forces (minimal rear area threat)
  },
  
  // Unit type vulnerability in rear area (some units are higher-value targets)
  unit_vulnerability: {
    HQ: 2.0,          // High-value target priority
    LOGISTICS: 1.5,   // Supply convoys targeted
    AIR_DEFENSE: 2.5, // SEAD priority
    ARTILLERY: 2.0,   // Counter-battery priority
    SIGNAL: 1.3,      // C2 targeting
    MEDICAL: 1.2,     // Theoretically protected but targeted in LSCO
    AVIATION: 1.8,    // Airfield attacks
    DEFAULT: 1.0
  }
};


/**
 * Distance curve of the rear-area threat (2026-10-11, Dr. Tatar: a curve, not steps).
 *   up to full_threat_km from the front line: 1.0
 *   beyond: floor + (1 - floor) x 0.5 ^ ((distance - full_threat_km) / half_distance_km)
 * Weapons with different reach (mortars, artillery, rockets, FPV drones, missiles) make the threat fall smoothly. The curve
 * is fitted to the earlier steps (15, 30, 60, 100 km: 1.0, 0.70, 0.40, 0.20, 0.10): at 30 km 0.64, 60 km 0.30, 100 km 0.16.
 * The floor stays above zero: strategic strikes and long-range drones reach everywhere. Both numbers are EDUCATED GUESSES
 * the planner can edit; a drone-saturated environment could stretch half_distance_km (not done: the technology rear
 * multiplier is used instead).
 */
export const REAR_AREA_DISTANCE_CURVE = {
  full_threat_km: 15,
  half_distance_km: 20,
  floor: 0.11,
  status: 'EDUCATED_GUESS',
  basis: 'Fitted to the earlier distance steps; no data by distance found (docs/research/RESEARCH_ARMOR_EQUIPMENT.md section 4).'
};

/**
 * Rear-area distance factor (1.0 near the front, falling to the floor far behind it).
 * @param {number} distanceKm distance behind the front line (FEBA), km, >= 0
 */
export function rearDistanceFactor(distanceKm, curve = REAR_AREA_DISTANCE_CURVE) {
  if (typeof distanceKm !== 'number' || Number.isNaN(distanceKm) || distanceKm < 0) {
    throw new Error(`rearAreaDecay.rearDistanceFactor: distance must be a number >= 0 (got: ${distanceKm})`);
  }
  if (distanceKm <= curve.full_threat_km) return 1.0;
  return curve.floor + (1 - curve.floor) * Math.pow(0.5, (distanceKm - curve.full_threat_km) / curve.half_distance_km);
}
